import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, Input, Textarea, Text, media } from '../../ui-components';
import { useFormik } from 'formik';
import { isEmailValid, isEmailDomainAllowed } from '../../utils/is-email-valid';
import contactValues from '../../static-content/contact.json';

/**
 * Component that renders a custom form posting to a Pardot form handler
 *
 * @component PardotFormHandler
 *
 * @param {object} props
 *
 * @prop {string} actionURL - The Pardot form handler endpoint URL
 */

const PardotFormHandler = ({ actionURL, formType, setFormComplete, geoSpace }) => {
  const [pardotFormComplete, setPardotFormComplete] = useState(false);
  const [formFlag, setFormFlag] = useState(false);
  
  const flag = {
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    company: false,
    title: false,
    webRegion: false,
    utmCampaign: false,
    utmMedium: false,
    utmSource: false,
    utmTerm: false,
    howCanWeHelpYou: false,
    yourLocation: false,
    marketingOptedin: false,
    hearAboutus: false,
    industry: false,
    employeesNo: false
  };
  let web_region_source = '',
    utm_campaign = '',
    utm_medium = '',
    utm_source = '',
    utm_term = '';
  let queryParams;
  const extractQueryParams = url => {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  };
  //Get the window URL and filter query params
  if (typeof window !== 'undefined') {
    queryParams = extractQueryParams(window.location.href);
    ({ web_region_source, utm_campaign, utm_medium, utm_source, utm_term } = queryParams);
  }
  //Switch function based on formType set flag.fields and setFormFlag as true
  const handleFormType = formType => {
    switch (formType) {
      case contactValues.PARDOT_BASIC_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.phone =
          flag.company =
          flag.title =
          flag.marketingOptedin =
            true;
        break;
      case contactValues.PARDOT_STANDARD_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.phone =
          flag.company =
          flag.title =
          flag.marketingOptedin =
          flag.howCanWeHelpYou =
          flag.yourLocation =
          flag.hearAboutus =
            true;
        break;
      case contactValues.PARDOT_TOF_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.company =
          flag.title =
          flag.marketingOptedin =
          flag.webRegion =
          flag.utmCampaign =
          flag.utmMedium =
          flag.utmSource =
          flag.utmTerm =
            true;
        break;
      case contactValues.PARDOT_MOF_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.company =
          flag.title =
          flag.marketingOptedin =
          flag.webRegion =
          flag.utmCampaign =
          flag.utmMedium =
          flag.utmSource =
          flag.utmTerm =
          flag.industry =
            true;
        break;
      case contactValues.PARDOT_BOF_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.company =
          flag.title =
          flag.marketingOptedin =
          flag.webRegion =
          flag.utmCampaign =
          flag.utmMedium =
          flag.utmSource =
          flag.utmTerm =
          flag.employeesNo =
            true;
        break;
      case contactValues.PARDOT_FAST_TRACK_FORM:
        flag.firstName =
          flag.lastName =
          flag.email =
          flag.company =
          flag.title =
          flag.marketingOptedin =
          flag.webRegion =
          flag.utmCampaign =
          flag.utmMedium =
          flag.utmSource =
          flag.utmTerm =
          flag.howCanWeHelpYou =
          flag.yourLocation =
          flag.hearAboutus =
            true;
        break;
      default:
        flag.firstName = flag.lastName = flag.email = flag.company = flag.title = flag.marketingOptedin = true;
    }
    useEffect(() => {
      setFormFlag(true);
    }, []);
  };
  // Set form flags based on formType recieved in the component
  handleFormType(formType);

  const validate = values => {
    const errors = {};
    if (flag.firstName && flag.lastName && flag.email && flag.company & flag.title) {
      if (!values.firstName) errors.firstName = 'This field is required';
      else if (values.firstName.length > 15) errors.firstName = 'Must be 15 characters or less';

      if (!values.lastName) errors.lastName = 'This field is required';
      else if (values.lastName.length > 20) errors.lastName = 'Must be 20 characters or less';

      if (!values.email) errors.email = 'This field is required';
      else if (!isEmailValid(values.email)) errors.email = 'Invalid email address';
      else if (!isEmailDomainAllowed(values.email)) errors.email = 'Only business email is accepted';

      if (!values.company) errors.company = 'This field is required';
      if (!values.title) errors.title = 'This field is required';
    }
    if (flag.phone) {
      if (values.phone.length > 13) errors.phone = 'Must be 13 digits or less';
    }
    if (flag.yourLocation && flag.howCanWeHelpYou) {
      if (!values.location) errors.location = 'This field is required';
      if (!values.howCanWeHelpYou) errors.howCanWeHelpYou = 'This field is required';
    }
    if (flag.employeesNo) {
      if (!values.employeesNo) errors.employeesNo = 'This field is required';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      title: '',
      webRegion: web_region_source || '',
      utmCampaign: utm_campaign || '',
      utmMedium: utm_medium || '',
      utmSource: utm_source || '',
      utmTerm: utm_term || '',
      howCanWeHelpYou: '',
      location: '',
      howDidYouHearAboutUs: '',
      marketingOptIn: false,
      industry: '',
      employeesNo: ''
    },
    validate,
    onSubmit: values => {
      fetch(actionURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(values).toString()
      })
        .then(() => {
          setPardotFormComplete(true);
          setFormComplete(true);
        })
        .catch(() => {
          setPardotFormComplete(true);
          setFormComplete(true);
        });
    }
  });

  return (
    <FormWrapper>
      {!pardotFormComplete && formFlag && (
        <form onSubmit={formik.handleSubmit}>
          <ContactFormInner>
            {flag.firstName && (
              <LabelInputContainer>
                <ContactLabel>First Name*</ContactLabel>
                <ContactInput {...formik.getFieldProps('firstName')} placeholder="Enter first name" />
                {formik.touched.firstName && formik.errors.firstName && (
                  <ErrorMessage>{formik.errors.firstName}</ErrorMessage>
                )}
              </LabelInputContainer>
            )}
            {flag.lastName && (
              <LabelInputContainer>
                <ContactLabel>Last Name*</ContactLabel>
                <ContactInput {...formik.getFieldProps('lastName')} placeholder="Enter last name" />
                {formik.touched.lastName && formik.errors.lastName && (
                  <ErrorMessage>{formik.errors.lastName}</ErrorMessage>
                )}
              </LabelInputContainer>
            )}
            {flag.email && (
              <LabelInputContainer>
                <ContactLabel>Email Address*</ContactLabel>
                <ContactInput {...formik.getFieldProps('email')} placeholder="Enter email address" />
                {formik.touched.email && formik.errors.email && <ErrorMessage>{formik.errors.email}</ErrorMessage>}
              </LabelInputContainer>
            )}
            {flag.phone && (
              <LabelInputContainer>
                <ContactLabel>Phone</ContactLabel>
                <ContactInput {...formik.getFieldProps('phone')} placeholder="Enter phone number" />
                {formik.touched.phone && formik.errors.phone && <ErrorMessage>{formik.errors.phone}</ErrorMessage>}
              </LabelInputContainer>
            )}
            {flag.company && (
              <LabelInputContainer>
                <ContactLabel>Company*</ContactLabel>
                <ContactInput {...formik.getFieldProps('company')} placeholder="Enter company name" />
                {formik.touched.company && formik.errors.company && (
                  <ErrorMessage>{formik.errors.company}</ErrorMessage>
                )}
              </LabelInputContainer>
            )}
            {flag.title && (
              <LabelInputContainer>
                <ContactLabel>Title*</ContactLabel>
                <ContactInput {...formik.getFieldProps('title')} placeholder="Enter job title" />
                {formik.touched.title && formik.errors.title && <ErrorMessage>{formik.errors.title}</ErrorMessage>}
              </LabelInputContainer>
            )}
            {flag.howCanWeHelpYou && (
              <LabelInputContainerFull>
                <ContactLabel>How can we help you?*</ContactLabel>
                <ContactTextArea
                  {...formik.getFieldProps('howCanWeHelpYou')}
                  placeholder="Write your comments here..."
                />
                {formik.touched.howCanWeHelpYou && formik.errors.howCanWeHelpYou && (
                  <ErrorMessage>{formik.errors.howCanWeHelpYou}</ErrorMessage>
                )}
              </LabelInputContainerFull>
            )}
            {flag.yourLocation && (
              <LabelInputContainerFull>
                <ContactLabel>Location*</ContactLabel>
                <ContactSelect {...formik.getFieldProps('location')}>
                  <option value="" label="Select your country" />
                  <option value="Australia" label="Australia" />
                  <option value="Germany" label="Germany" />
                  <option value="EMA (Non UK or Germany)" label="EMA (Non UK or Germany)" />
                  <option value="New Zealand" label="New Zealand" />
                  <option value="United Kingdom" label="United Kingdom" />
                  <option value="United States" label="United States" />
                  <option value="APAC" label="APAC" />
                </ContactSelect>
                {formik.touched.location && formik.errors.location && (
                  <ErrorMessage>{formik.errors.location}</ErrorMessage>
                )}
              </LabelInputContainerFull>
            )}
            {flag.hearAboutus && (
              <LabelInputContainerFull>
                <ContactLabel>How did you hear about us?</ContactLabel>
                <ContactTextArea
                  {...formik.getFieldProps('howDidYouHearAboutUs')}
                  placeholder="Enter how you heard about us"
                />
              </LabelInputContainerFull>
            )}
            {flag.industry && (
              <LabelInputContainer>
                <ContactLabel>Industry</ContactLabel>
                <ContactSelect {...formik.getFieldProps('industry')}>
                  <option value="" label="Select Industry" />
                  <option value="Aerospace & Defence" label="Aerospace & Defence" />
                  <option value="Construction" label="Construction" />
                  <option value="Contractor (Associate)" label="Contractor (Associate)" />
                  <option value="Electronics" label="Electronics" />
                  <option value="Energy" label="Energy" />
                  <option value="Finance" label="Finance" />
                  <option value="Gaming & Gambling" label="Gaming & Gambling" />
                  <option value="Government" label="Government" />
                  <option value="Health" label="Health" />
                  <option value="Information Technology" label="Information Technology" />
                  <option value="Insurance" label="Insurance" />
                  <option value="Legal" label="Legal" />
                  <option value="Leisure" label="Leisure" />
                  <option value="Logistics" label="Logistics" />
                  <option value="Management Consultancy" label="Management Consultancy" />
                  <option value="Manufacturing" label="Manufacturing" />
                  <option value="Media" label="Media" />
                  <option value="Pharmaceutical" label="Pharmaceutical" />
                  <option value="Retail" label="Retail" />
                  <option value="Telecommunications" label="Telecommunications" />
                  <option value="Transportation" label="Transportation" />
                  <option value="Other" label="Other" />
                </ContactSelect>
              </LabelInputContainer>
            )}
            {flag.employeesNo && (
              <LabelInputContainer>
                <ContactLabel>No of Employees*</ContactLabel>
                <ContactSelect {...formik.getFieldProps('employeesNo')}>
                  <option value="" label="Select No of Employees" />
                  <option value="1-49" label="1-49" />
                  <option value="50-249" label="50-249" />
                  <option value="250-499" label="250-499" />
                  <option value="500+" label="500+" />
                </ContactSelect>
                {formik.touched.employeesNo && formik.errors.employeesNo && (
                  <ErrorMessage>{formik.errors.employeesNo}</ErrorMessage>
                )}
              </LabelInputContainer>
            )}
            {flag.webRegion && <ContactInputUtm {...formik.getFieldProps('webRegion')} placeholder="webRegion" />}
            {flag.utmCampaign && <ContactInputUtm {...formik.getFieldProps('utmCampaign')} placeholder="utmCampaign" />}
            {flag.utmMedium && <ContactInputUtm {...formik.getFieldProps('utmMedium')} placeholder="utmMedium" />}
            {flag.utmSource && <ContactInputUtm {...formik.getFieldProps('utmSource')} placeholder="utmSource" />}
            {flag.utmTerm && <ContactInputUtm {...formik.getFieldProps('utmTerm')} placeholder="utmTerm" />}
            <ContactCheckboxContainer>
              <DisclaimerText>{contactValues.CONTACT_FORM_DISCLAIMER_TEXT}</DisclaimerText>
              {flag.marketingOptedin && (
                <ContactCheckbox>
                  <DisclaimerInput type="checkbox" {...formik.getFieldProps('marketingOptIn')} />
                  <ContactLabel>{contactValues.CONTACT_FORM_AGREE}.</ContactLabel>
                </ContactCheckbox>
              )}
              <DisclaimerText>
                {contactValues.CONTACT_FORM_UNSUBSCRIBE_TEXT}
                {geoSpace === 'en-us' ? <a href="https://www.credera.com/privacy-notice">Privacy Policy.</a> : <a href={`https://www.credera.com${geoSpace ? '/' + geoSpace : ''}/europe-asia-pacific-privacy-notice`}>Europe | Asia-Pacific Privacy Notice.</a>} 
              </DisclaimerText>
            </ContactCheckboxContainer>
            <Input isSubmitType type="submit" value="Submit" />
          </ContactFormInner>
        </form>
      )}
      {pardotFormComplete && <CompleteMessage>Thank you for contacting us, we will reach out soon</CompleteMessage>}
    </FormWrapper>
  );
};

const FormWrapper = styled.div``;
const ContactFormInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
`;
const LabelInputContainer = styled.div`
  flex: 1 1 calc(50% - 28px);
  display: flex;
  flex-direction: column;
  @media (max-width: ${media.mediumBottom}) {
    flex: 1 calc(100%);
    width: 100%;
  }
`;
const LabelInputContainerFull = styled.div`
  flex: 1 calc(100%);
  display: flex;
  flex-direction: column;
`;
const ContactLabel = styled.label`
  color: ${colors.midnightBlack};
  font-size: 14px;
`;
const ContactInput = styled(Input)`
  margin-bottom: 28px;
`;
const ContactInputUtm = styled(Input)`
  display: none;
`;
const ContactTextArea = styled(Textarea)`
  margin: 0 0 20px;
`;
const ContactSelect = styled.select`
  width: 100%;
  padding: 10px 16px;
  color: ${colors.midnightBlack};
  border: 1px solid ${colors.gray3};
  font-size: 16px;
  margin-bottom: 28px;
  ::placeholder {
    color: ${colors.gray2};
  }
  &:focus,
  &:active {
    outline: 0;
    border: 1px solid ${colors.crederaRed};
  }
`;
const DisclaimerText = styled(Text)`
a {
  color: ${colors.linkDefault};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
`;

const ContactCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: 1.45rem;
  height: 20px;
`;
const ContactCheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 14px;
  margin-bottom: 28px;
`;
const DisclaimerInput = styled(Input)`
  margin: 8px 0 5px;
`;
const ErrorMessage = styled.p`
  color: ${colors.crederaRed};
  font-size: 14px;
  font-weight: 700;
`;
const CompleteMessage = styled.p`
  color: ${colors.midnightBlack};
  font-size: 24px;
  font-weight: 400;
`;

export default PardotFormHandler;
